import {
  type AirportFacet,
  type Maybe,
  type NightFacet,
  type Receipt,
  type SaleCalendar,
} from '@gql/schema';
import { type ITranslationsObject, i18n } from '@secretescapes/i18n';
import { formatMomentToString } from '@utils/dates.utils';
import { minBy } from '@utils/lodash.utils';
import { numberToCurrency } from '@utils/pricing.utils';
import {
  deleteSearchParam,
  getParam,
  hasSearchParam,
  navigateReplace,
  setSearchParamValue,
} from '@utils/url.utils';
import moment from 'moment/moment';
import { type URLParams } from './AvailabilityPresentation.interfaces';
import { type Option } from './AvailabilitySearchBar/DropdownInput';
import { type CalendarURLParams } from './Calendar/Calendar.interfaces';
import { type ExtendedOffer } from './Offers';

export const CALENDAR_DAY_DIMENSIONS_PX = 48;
export const ALL_OFFERS = 'all';
export const ALL_DURATIONS = 'all';

/**
 * Calendar should not go past last check-out date,
 * else not go past last available check-in date
 */
export const getLastDatesToShow = (
  availability: Maybe<SaleCalendar>,
  lastCheckOutDate: Maybe<string>
) => {
  return shouldUseLastCheckOutDate(availability, lastCheckOutDate)
    ? moment(lastCheckOutDate)
    : availability?.lastAvailableCheckInDate
      ? moment(availability?.lastAvailableCheckInDate)
      : moment();
};

export const shouldUseLastCheckOutDate = (
  availability: Maybe<SaleCalendar>,
  lastCheckOutDate: Maybe<string>
) => {
  return (
    !!lastCheckOutDate && moment(lastCheckOutDate) > moment(availability?.lastAvailableCheckInDate)
  );
};

export const setCalendarURLParams = ({ startDate, endDate }: CalendarURLParams) => {
  if (!startDate && !endDate) {
    navigateReplace(deleteSearchParam('checkin', 'checkout'));
    return;
  }

  const formattedStartDate = formatMomentToString(startDate);
  const formattedEndDate = formatMomentToString(endDate);

  if (formattedStartDate) {
    const checkInDateUrl = setSearchParamValue('checkin', formattedStartDate);

    if (!formattedEndDate) {
      checkInDateUrl.searchParams.delete('checkout');
    }

    navigateReplace(checkInDateUrl);
  }

  if (formattedEndDate) {
    const checkOutDateUrl = setSearchParamValue('checkout', formattedEndDate);
    navigateReplace(checkOutDateUrl);
  }
};

export const getFinalRates = (
  receipt: Maybe<Receipt>,
  numberOfRooms: number,
  currency: string,
  territoryLocale: string
) => {
  if (!receipt?.totalAmount || !receipt.nights) {
    return undefined;
  }

  const finalRatePerRoomPerNight = numberToCurrency(
    Math.ceil(receipt.totalAmount / (numberOfRooms * receipt.nights)),
    currency,
    territoryLocale,
    true
  );

  return {
    finalRate: receipt.totalAmountForDisplay ?? '',
    finalRatePerRoomPerNight,
  };
};

export const getCheapestFinalRates = (
  receipts: Receipt[],
  numOfRooms: number,
  currency: string,
  territoryLocale: string
) => {
  if (receipts.length === 0) {
    return undefined;
  }

  const cheapestReceipt = minBy(receipts, receipt => receipt.totalAmount);

  if (!cheapestReceipt?.totalAmount || !cheapestReceipt.nights) {
    return undefined;
  }

  const cheapestFinalRatePerRoomPerNight = numberToCurrency(
    Math.ceil(cheapestReceipt.totalAmount / (numOfRooms * cheapestReceipt.nights)),
    currency,
    territoryLocale,
    true
  );

  return {
    cheapestFinalRate: cheapestReceipt.totalAmountForDisplay ?? '',
    cheapestFinalRatePerRoomPerNight,
  };
};

export function notEmpty<T>(value: Maybe<T>): value is T {
  return !!value;
}

export const determineMaxRoomCapacity = (offers: ExtendedOffer[]) => {
  const hasReceipt = offers.some(offer => !!offer.receipt?.maxAvailability);

  return {
    rooms: offers.reduce((max, offer) => {
      const source = hasReceipt
        ? (offer.receipt?.maxAvailability ?? 0)
        : (offer.pricing?.maxAvailability ?? 0);

      return Math.max(max, source);
    }, 0),
  };
};
export const getURLParams = (): URLParams => {
  return {
    tripTypeParam: getParam('tripType'),
    airportCodeParam: getParam('airportCode'),
    durationParam: getParam('duration'),
    airportNameParam: getParam('airportName'),
  };
};

export const urlParamsExist = () => {
  return hasSearchParam('airportCode', 'duration', 'airportName');
};

export const deleteURLParams = () => {
  const updatedUrl = deleteSearchParam('airportCode', 'duration', 'airportName');

  navigateReplace(updatedUrl);
};

export const getAirportOptions = (
  data: Maybe<AirportFacet[]>,
  translations: ITranslationsObject
): Option[] => {
  return (data ?? [])
    .filter(entry => entry.amount)
    .map(entry => ({
      value: entry.code ?? '',
      text: entry.name ?? '',
      subtext: `${i18n('common.fromPrice.label', [], translations)} ${entry.amountForDisplay}`,
    }));
};

export const getNightOptions = (
  data: Maybe<NightFacet[]>,
  translations: ITranslationsObject
): Option[] => {
  return (data ?? [])
    .filter(entry => entry.amount)
    .map(entry => ({
      value: entry.nights?.toString() ?? ALL_DURATIONS,
      text: entry.nights
        ? i18n('common.nights.text', [entry.nights], translations)
        : i18n('flights.all.durations', [], translations),
      subtext: `${i18n('common.fromPrice.label', [], translations)} ${entry.amountForDisplay}`,
    }));
};
